import React, { useCallback } from 'react';
import { Box, Grid, Heading, HStack, VStack, Text, Link, useColorMode } from '@chakra-ui/react';
import { format } from 'date-fns';

import { __ } from '../../../lib/i18n';
import { LocationPinIcon, ClockIcon, CompassIcon } from '../../icons';
import { useCalendarSettings, useCustomToast, useIconColor } from '../../../lib';

import type { IEvent } from '../../../lib';

interface ICardsGridSectionProps {
	event: IEvent;
	isSameDayEvent: boolean;
	dateFormat: string;
	timeFormat: string;
	timeLabel: string;
	locationLabel: string;
}

export const CardsGridSection: React.FunctionComponent<ICardsGridSectionProps> = ({
	event,
	isSameDayEvent,
	dateFormat,
	timeFormat,
	timeLabel,
	locationLabel,
}) => {
	const { colorMode } = useColorMode();
	const { settings } = useCalendarSettings();
	const { showToast } = useCustomToast();
	const color = useIconColor();

	const handleSubmit = useCallback(() => {
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const { latitude, longitude } = position.coords;

					// Construct the destination address from event details
					const { address, city, state, country } = event;
					const destinationParts = [address, city, state, country].filter(Boolean);
					const fullDestination = destinationParts.join(', ');

					const encodedDestinationAddress = encodeURIComponent(fullDestination);
					if (encodedDestinationAddress) {
						const url = `https://www.google.com/maps/dir/?api=1&origin=${latitude},${longitude}&destination=${encodedDestinationAddress}`;
						window.open(url, '_blank');
					}
				},
				(error) => {
					console.error('Error getting location:', error);
					showToast({
						title: '',
						description: __('Unable to get your location. Please enable GPS and try again.'),
						status: 'error',
					});
				}
			);
		} else {
			showToast({
				title: '',
				description: __('Geolocation is not supported by your browser.'),
				status: 'error',
			});
		}
	}, [event, showToast]);

	const { eventModalCardsColor } = settings.styles[colorMode];
	return (
		<Grid templateColumns='repeat(2, 1fr)' gap={4} p={2}>
			<Box p={6} bg={eventModalCardsColor} borderRadius='xl' height='150px'>
				<HStack alignItems='flex-start'>
					<ClockIcon size={24} fill={color} />
					<VStack alignItems='flex-start'>
						<Heading color={color} fontSize='larger'>
							{timeLabel}
						</Heading>
						<Text m={0} mb={8} color={colorMode === 'light' ? '#656565' : 'white'} fontSize='14px'>
							{format(event.start, isSameDayEvent ? timeFormat : `${dateFormat} ${timeFormat}`)} -{' '}
							{format(event.end, isSameDayEvent ? timeFormat : `${dateFormat} ${timeFormat}`)}
							{event.timezone && ` (${event.timezone})`}
						</Text>
					</VStack>
				</HStack>
			</Box>
			<Box p={6} bg={eventModalCardsColor} borderRadius='xl' height='150px'>
				<VStack alignItems='flex-start' spacing={2}>
					<HStack alignItems='flex-start'>
						<LocationPinIcon size={24} fill={color} />
						<VStack alignItems='flex-start' justifyContent='space-between' spacing={1}>
							<Heading color={color} fontSize='larger'>
								{locationLabel}
							</Heading>
							{event.venue?.name ? (
								<VStack alignItems='flex-start' spacing={0}>
									<Text
										m={0}
										color={colorMode === 'light' ? '#656565' : 'white'}
										fontSize='14px'
										fontWeight='600'
									>
										{event.venue.name}
									</Text>
									{(event.venue.address ||
										event.venue.city ||
										event.venue.state ||
										event.venue.country) && (
										<Text m={0} color={colorMode === 'light' ? '#656565' : 'white'} fontSize='12px'>
											{[
												event.venue.address,
												event.venue.city,
												event.venue.state,
												event.venue.country,
											]
												.filter(Boolean)
												.join(', ')}
										</Text>
									)}
								</VStack>
							) : (
								<Text m={0} color={colorMode === 'light' ? '#656565' : 'white'} fontSize='14px'>
									{__('Not Specified. For more details, please contact the organizer.')}
								</Text>
							)}
						</VStack>
					</HStack>
					{event.venue && (
						<HStack alignItems='center'>
							<CompassIcon size={16} fill={color} />
							<Link fontWeight={700} fontSize='sm' color='blue.500' onClick={handleSubmit}>
								{__('Get Directions')}
							</Link>
						</HStack>
					)}
				</VStack>
			</Box>
		</Grid>
	);
};
